import { useCallback } from 'react';

export interface ScrollAnchor {
  sectionId: string;
  type: string;
  topOffset: number; // ratio of container viewport height (0 = anchor at top, 1 = one viewport below)
}

export const useScrollAnchors = () => {
  const findAnchorAtScroll = useCallback((container: HTMLDivElement | null): ScrollAnchor | null => {
    if (!container) return null;

    const anchors = container.querySelectorAll<HTMLElement>('[data-section-anchor]');
    if (anchors.length === 0) return null;

    const scrollTop = container.scrollTop;
    const containerHeight = container.clientHeight;
    const viewportMid = scrollTop + containerHeight / 2;

    let closestAnchor: HTMLElement | null = null;
    let closestDistance = Infinity;

    anchors.forEach((anchor) => {
      const rect = anchor.getBoundingClientRect();
      const anchorAbsoluteTop = scrollTop + rect.top - container.getBoundingClientRect().top;
      const distance = Math.abs(anchorAbsoluteTop - viewportMid);

      if (distance < closestDistance) {
        closestDistance = distance;
        closestAnchor = anchor;
      }
    });

    if (!closestAnchor) return null;

    const rect = closestAnchor.getBoundingClientRect();
    const anchorAbsoluteTop = scrollTop + rect.top - container.getBoundingClientRect().top;
    const topOffset = (scrollTop - anchorAbsoluteTop) / containerHeight;

    return {
      sectionId: closestAnchor.dataset.sectionAnchor || '',
      type: closestAnchor.dataset.sectionType || 'UNKNOWN',
      topOffset,
    };
  }, []);

  const scrollToAnchor = useCallback(
    (container: HTMLDivElement | null, anchor: ScrollAnchor, smooth = true): boolean => {
      if (!container) return false;

      const targetAnchor = container.querySelector(
        `[data-section-anchor="${anchor.sectionId}"]`
      ) as HTMLElement | null;

      if (!targetAnchor) return false;

      const rect = targetAnchor.getBoundingClientRect();
      const scrollTop = container.scrollTop;
      const anchorAbsoluteTop = scrollTop + rect.top - container.getBoundingClientRect().top;
      const targetScroll = anchorAbsoluteTop + anchor.topOffset * container.clientHeight;

      if (smooth) {
        container.scrollTo({ top: targetScroll, behavior: 'smooth' });
      } else {
        container.scrollTop = targetScroll;
      }
      return true;
    },
    []
  );

  return { findAnchorAtScroll, scrollToAnchor };
};
